import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

type BreadcrumbEntityLabelContextValue = {
  label: string | null;
  setLabel: (label: string | null) => void;
};

const BreadcrumbEntityLabelContext = createContext<BreadcrumbEntityLabelContextValue | null>(null);

export const BreadcrumbEntityLabelProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [label, setLabelState] = useState<string | null>(null);
  const setLabel = useCallback((next: string | null) => {
    setLabelState((previous) => (previous === next ? previous : next));
  }, []);
  const value = useMemo(
    () => ({
      label,
      setLabel,
    }),
    [label, setLabel],
  );

  return (
    <BreadcrumbEntityLabelContext.Provider value={value}>
      {children}
    </BreadcrumbEntityLabelContext.Provider>
  );
};

export const useBreadcrumbEntityLabel = () => {
  const context = useContext(BreadcrumbEntityLabelContext);

  if (!context) {
    throw new Error("useBreadcrumbEntityLabel must be used within BreadcrumbEntityLabelProvider");
  }

  return context;
};

export const useSetCurrentCrumbLabel = (label?: string | null) => {
  const setLabel = useContext(BreadcrumbEntityLabelContext)?.setLabel;

  useLayoutEffect(() => {
    if (!setLabel) {
      return undefined;
    }

    setLabel(label?.trim() || null);

    return () => setLabel(null);
  }, [label, setLabel]);
};
